import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { DeleteSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { readImageFiles } from '../../../utils/fileDropBrowserUtils';
import { getFiles, SUPPORTED_IMAGE_MIMETYPES } from '../../../utils/fileDropUtils';
import { Button } from '../../_atoms/Button';
import FileDropComponent from '../../_atoms/FileDropComponent/FileDropComponent';
import MaxSizeMediaContent from '../../_molecules/MaxSizeMediaContent';
import { generateUniqueId } from '../../SlideComponents/slideUtil';

export type UploadFilePickerFile = {
  src: string;
  mimeType?: string;
  fileId: string;
  width?: number;
  height?: number;
  skipUpload?: boolean;
};

const UploadFilePicker = ({
  droppedImageData,
  onFileSelected,
  fileId,
  onDelete,
}: {
  droppedImageData?: UploadFilePickerFile;
  onFileSelected: (data?: UploadFilePickerFile) => void;
  fileId?: string;
  onDelete?: () => void;
}) => {
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const [imageData, _setImageData] = useState<UploadFilePickerFile | undefined>();
  const [file, setFile] = useState<File>();
  const [droppedFile, setDroppedFile] = useState<File | null>();

  const setImageData = useCallback(
    (data?: UploadFilePickerFile) => {
      _setImageData(data);
      onFileSelected(data);
    },
    [onFileSelected],
  );
  useEffect(() => {
    if (droppedImageData) {
      // Parent component drops an image file and wants to do something in this component.
      setImageData({ ...droppedImageData, skipUpload: true });
    }
  }, [droppedImageData]);

  useEffect(() => {
    if (file) {
      readImageFiles(file)
        .then(readFile => {
          const unique = generateUniqueId();
          setImageData({
            src: readFile.src,
            mimeType: file.type,
            fileId: `${unique}_${file.name}`,
          });
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
    }
  }, [file]);
  useEffect(() => {
    if (droppedFile) {
      setFile(droppedFile);
    }
  }, [droppedFile, setFile]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files.item(0);
      if (file) {
        setFile(file);
      }
    }
  };

  const handleActivate = () => {
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = '';
      hiddenFileInputRef.current!.click();
    }
  };

  const handleFilesDropped = (files: FileList | null) => {
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !SUPPORTED_IMAGE_MIMETYPES.includes(file.type),
      );
      if (invalidFiles.length > 0) {
        alert('Invalid file type, please try again');
        return;
      }

      if (parsedFiles.length > 0) {
        setDroppedFile(parsedFiles[0]);
      }
    }
  };
  const handleDelete = () => {
    setImageData(undefined);
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <Container>
      {!imageData && (
        <FileDropComponentContainer>
          <input
            ref={hiddenFileInputRef}
            type="file"
            name="myFile"
            multiple={false}
            accept={SUPPORTED_IMAGE_MIMETYPES.join(',')}
            onChange={onChange}
            style={{ display: 'none' }}
          />
          <FileDropComponent
            onClick={handleActivate}
            onFilesDropped={handleFilesDropped}
          />
        </FileDropComponentContainer>
      )}
      {imageData && (
        <>
          <FileDropComponentContainer>
            <ImageCropContainer>
              {imageData && (
                <div style={{ width: '100%', height: '30vh' }}>
                  <MaxSizeMediaContent>
                    <img
                      src={imageData.src}
                      style={{ height: '100%', width: '100%', objectFit: 'contain' }}
                    />
                    <DeleteButton type="ghost" onClick={handleDelete}>
                      <DeleteSvg />
                    </DeleteButton>
                  </MaxSizeMediaContent>
                </div>
              )}
            </ImageCropContainer>
          </FileDropComponentContainer>
        </>
      )}
    </Container>
  );
};

export default UploadFilePicker;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 5px;
  svg {
    margin: 0;
  }
  width: 30px;
  height: 30px;
`;

const ImageCropContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  & img {
    max-height: 100%;
    max-width: 100%;
    border-radius: 5px;
  }

  /* Make sure react crop positions the image according to the max size image positioner */

  .ReactCrop {
    height: 100%;
    width: 100%;
  }

  .ReactCrop__crop-selection {
    border: 3px solid ${theme.colors.blue};
  }

  .ReactCrop__drag-handle::after {
    border-radius: 5000px;
    background-color: ${theme.colors.blue};
    border: 1px solid ${theme.colors.blue};
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FileDropComponentContainer = styled.div`
  width: 100%;
  flex: 1 1 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;
